import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, forwardRef, useState, useEffect, useMemo, } from "react";
import DayPicker from "react-day-picker/DayPicker";
import { format } from "date-fns";
import { styled } from "../theme";
import { YearMonthPicker } from "../year-month-picker";
import { DATE_FORMAT } from "./date";
/**
 * A component that allows users to select a date from a calendar.
 *
 * @param {Props} props The props for the component.
 * @param {Ref<HTMLDivElement>} ref The ref for the date picker container element.
 * @returns {JSX.Element} The rendered date picker component.
 */
export const DatePicker = forwardRef(({ date, range, style, tabIndex, testId, onChange, className }, ref) => {
    const pickerRef = useRef();
    /**
     * We keep clone of the date, so we don't rely on parent storing the date and providing it back on every change.
     * Required to render the YearMonthSelect correctly.
     */
    const [dateClone, setDateClone] = useState(date);
    const [yearMonthDateClone, setYearMonthDateClone] = useState(() => date ?? new Date());
    const day = format(date, DATE_FORMAT.ISO_DATE);
    // prevent element loosing focus for select elements https://github.com/gpbl/react-day-picker/issues/839
    const captionElement = useMemo(() => ({ date }) => (_jsx(CaptionForm, { className: "DayPicker-Caption", children: _jsx(YearMonthPicker, { date: date, range: range, tabIndex: tabIndex, onChange: date => {
                setYearMonthDateClone(date);
            } }) })), [range, tabIndex]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setDateClone(date);
        setYearMonthDateClone(date);
    }, [date, day]);
    return (_jsx(Container, { ref: ref, style: style, "data-test-id": testId && `datepicker-${testId}`, className: className, children: _jsx(DayPicker, { tabIndex: tabIndex, ref: pickerRef, month: yearMonthDateClone, selectedDays: [dateClone], navbarElement: () => null, onDayClick: (date) => {
                setDateClone(date);
                setYearMonthDateClone(date);
                onChange(date, "day");
            }, onMonthChange: (date) => {
                setYearMonthDateClone(date);
            }, captionElement: captionElement }) }));
});
DatePicker.defaultProps = {
    tabIndex: 0,
};
/**
 * The container for the date picker.
 */
export const Container = styled.div `
  .DayPickerInput {
    width: 100%;
  }

  .DayPicker {
    background: ${({ theme }) => theme.color.white};
    border: 1px solid ${({ theme }) => theme.color.gray80};
  }

  .DayPicker-Wrapper:focus {
    outline: none;
  }

  .DayPicker-Weekday {
    color: ${({ theme }) => theme.color.gray100};
  }

  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--outside
    ) {
    background: ${({ theme }) => theme.color.primary};
  }

  .DayPicker-Day--today {
    color: ${({ theme }) => theme.color.deepOrange100};
  }

  .DayPicker:not(.DayPicker--interactionDisabled) {
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(
        .DayPicker-Day--selected
      ):not(.DayPicker-Day--outside):hover {
      background: ${({ theme }) => theme.color.lightBlue10};
    }
  }
`;
/**
 * The form for the date picker caption.
 */
export const CaptionForm = styled.div `
  display: table-caption;
  margin-bottom: 15px;
  padding: 0;
  font-size: 14px;
`;
